import { type DateRange } from 'react-day-picker';
import { type HTMLAttributes } from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const DatePickerWithRange = ({
  className,
  date,
  setDate,
  onDateChange,
  onClear,
  isOpen,
  setIsOpen,
  dataCy,
  startMonth,
  endMonth,
}: HTMLAttributes<HTMLDivElement> & {
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  onDateChange: (date: DateRange | undefined) => void;
  onClear: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  dataCy?: string;
  startMonth?: Date;
  endMonth?: Date;
}) => {
  const tSearch = useTranslations('search');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          id="date"
          variant="outline-solid"
          data-cy={dataCy ?? 'date-picker-with-range-button'}
          className={cn(
            'flex h-auto w-full justify-start gap-3 rounded-lg px-3 text-left font-normal text-text-base dark:text-text-base dark:hover:text-text-base',
            className,
            !date && 'text-muted-foreground',
          )}
          icon={CalendarIcon}
          iconClassName="icon-4"
        >
          {date?.from ? (
            date.to ? (
              `${format(date.from, 'LLL dd, y')} - ${format(date.to, 'LLL dd, y')}`
            ) : (
              format(date.from, 'LLL dd, y')
            )
          ) : (
            <span>{tSearch('pickDate')}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          defaultMonth={date?.from}
          selected={date}
          onSelect={(range) => {
            setDate(range);
            onDateChange(range);
          }}
          numberOfMonths={2}
          startMonth={startMonth}
          endMonth={endMonth}
        />
        <Button
          onClick={onClear}
          variant="navigation-action-primary"
          className="mx-auto mb-4 flex focus-visible:ring-offset-ui-container"
          disabled={!date}
          data-cy="date-picker-with-range-clear-button"
          icon={X}
          iconClassName="icon-4"
        >
          {tSearch('clearDateRange')}
        </Button>
      </PopoverContent>
    </Popover>
  );
};
